<template>
  <div class="credentials-modal">
    <h2 class="credentials-modal__title">Защитите доступ</h2>

    <form class="credentials-form" @submit.prevent="handleSubmit">
      <label class="credentials-form__label" for="login">Логин</label>
      <input
        id="login"
        v-model.trim="form.login"
        class="credentials-form__input"
        type="text"
        autocomplete="username"
        placeholder="Например, vpn_user"
        :disabled="isSubmitting"
      />

      <label class="credentials-form__label" for="password">Пароль</label>
      <input
        id="password"
        v-model="form.password"
        class="credentials-form__input"
        type="password"
        autocomplete="new-password"
        placeholder="Не менее 8 символов"
        :disabled="isSubmitting"
      />

      <label class="credentials-form__label" for="confirm">Повторите пароль</label>
      <input
        id="confirm"
        v-model="form.confirmPassword"
        class="credentials-form__input"
        type="password"
        autocomplete="new-password"
        placeholder="Повторите пароль"
        :disabled="isSubmitting"
      />

      <ul class="credentials-form__rules">
        <li>Пароль от 8 до 32 символов</li>
        <li>Добавьте буквы в разном регистре, цифры и символы</li>
      </ul>

      <p v-if="errorMessage" class="credentials-form__error">
        {{ errorMessage }}
      </p>

      <BaseButton
        text="Сохранить данные"
        styles="primary-gradient"
        :is-disabled="isSubmitDisabled"
        full-width
        vibration="success"
        @on-submit="handleSubmit"
      />
    </form>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref, computed } from 'vue'
import { useAuth } from '@/shared/lib/auth/useAuth'
import BaseButton from '@/ui/components/Base/BaseButton.vue'
import { useModal } from '@/shared/lib/modal/useModal'
import { BROWSER_LOGIN_URL, BROWSER_LOGIN_URL_DEV, BROWSER_LOGIN_URL_PROD } from '@/shared/config/browserLinks'

const emit = defineEmits<{
  (e: 'close'): void
}>()

const { setCredentials } = useAuth()
const { openAuthSuccessModal } = useModal()

const form = reactive({
  login: '',
  password: '',
  confirmPassword: '',
})

const isSubmitting = ref(false)
const errorMessage = ref('')

const isPasswordValid = computed(() => {
  return form.password.length >= 8 && form.password.length <= 32
})

const isSubmitDisabled = computed(() => {
  return (
    !form.login ||
    !form.password ||
    !form.confirmPassword ||
    form.password !== form.confirmPassword ||
    !isPasswordValid.value ||
    isSubmitting.value
  )
})

const showSuccessModal = () => {
  openAuthSuccessModal({
    title: 'Доступ защищён',
    description: 'Логин и пароль сохранены. Теперь можно входить через браузер.',
    buttonLink: BROWSER_LOGIN_URL,
    secondaryLinks: [
      { label: 'Тест: dev.testbot.podpiska.vip', href: BROWSER_LOGIN_URL_DEV },
      { label: 'Прод: bot.podpiska.vip', href: BROWSER_LOGIN_URL_PROD }
    ]
  })
}

const handleSubmit = async () => {
  if (isSubmitDisabled.value) {
    return
  }

  isSubmitting.value = true
  errorMessage.value = ''

  try {
    const response = await setCredentials({
      login: form.login,
      password: form.password,
    })

    if (response.success) {
      showSuccessModal()
      emit('close')
    } else {
      errorMessage.value = response.message || 'Не удалось сохранить данные.'
    }
  } catch (error) {
    if (error instanceof Error) {
      errorMessage.value = error.message
    } else {
      errorMessage.value = 'Не удалось сохранить данные.'
    }
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style lang="scss" scoped>
.credentials-modal {
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: $neutral-100;

  &__title {
    @include h1;
    margin: 0;
    text-align: center;
  }

  &__description {
    @include p2-regular;
    margin: 0;
    color: $neutral-60;
    text-align: left;

    a {
      color: $primary-100;
      text-decoration: underline;
    }
  }

  &__success {
    @include p2-regular;
    color: $primary-100;
    margin: 0;
    text-align: center;
  }
}

.credentials-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: left;

  &__label {
    @include p2-medium;
    color: $neutral-100;
  }

  &__input {
    width: 100%;
    height: 48px;
    padding: 12px 16px;
    border-radius: 12px;
    border: 2px solid $secondary-200;
    background: $secondary-200;
    color: $neutral-100;
    font-size: 16px;
    transition: border-color 0.2s ease;

    &:focus {
      outline: none;
      border-color: $primary-100;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    &::placeholder {
      color: $neutral-60;
    }
  }

  &__rules {
    margin: 4px 0 0;
    padding-left: 20px;
    list-style: disc;
    color: $neutral-60;
    @include p3-regular;
  }

  &__error {
    @include p3-regular;
    color: #ff4d4f;
    margin: 4px 0 0;
  }
}
</style>

